// AGC: ConsultationsList - List of user's consultations
// AGC: Shows active and past consultations with status

import React, { useState, useEffect } from 'react'
import { 
  MessageCircle, 
  Clock, 
  CheckCircle,
  AlertTriangle,
  Plus,
  RefreshCw,
  ChevronRight,
} from 'lucide-react'
import { TopBar, GlassCard } from '../shared'
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { 
  type Consultation,
  STATUS_INFO,
  CONSULTATION_TYPE_INFO,
  URGENCY_INFO,
} from '../../types/advisor'
import type { Screen } from '../../types'
import { getBackendUrl } from '../../utils/backendUrl'

interface ConsultationsListProps {
  onNavigate: (screen: Screen, params?: Record<string, unknown>) => void
  onBack: () => void
}

const BACKEND_URL = getBackendUrl()

/**
 * AGC: Consultations List Component
 */
export const ConsultationsList: React.FC<ConsultationsListProps> = ({
  onNavigate,
  onBack,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [consultations, setConsultations] = useState<Consultation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all')

  /**
   * AGC: Fetch consultations
   */
  const fetchConsultations = async () => {
    if (!user?.uid) return
    
    setIsLoading(true)
    setError(null)

    try {
      let statusFilter = ''
      if (filter === 'active') {
        statusFilter = 'pending,bidding,accepted,in_progress,delivered'
      } else if (filter === 'completed') {
        statusFilter = 'completed,cancelled'
      }

      const params = new URLSearchParams({
        userId: user.uid,
        role: 'farmer',
      })
      if (statusFilter) params.append('status', statusFilter)

      const response = await fetch(`${BACKEND_URL}/api/v1/advisors/consultations/list?${params}`)
      const data = await response.json()

      if (data.success) {
        setConsultations(data.consultations)
      } else {
        setError(data.error || 'Erro ao carregar consultas')
      }
    } catch (err) {
      console.error('Error fetching consultations:', err)
      setError('Erro de conexão')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchConsultations()
  }, [user?.uid, filter])

  /**
   * AGC: View consultation detail
   */
  const handleViewDetail = (consultationId: string) => {
    onNavigate('consultation-detail' as Screen, { consultationId })
  }

  /**
   * AGC: Create new consultation
   */
  const handleNewConsultation = () => {
    onNavigate('advisor-marketplace' as Screen)
  }

  // AGC: Filter tabs — ids typed for setFilter compatibility
  const filters: Array<{ id: 'all' | 'active' | 'completed'; label: string }> = [
    { id: 'all', label: t('common.all') },
    { id: 'active', label: t('common.active') },
    { id: 'completed', label: t('tasks.statusCompleted') },
  ]

  return (
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 transition-colors">
      <TopBar
        title={t('advisor.myConsultations') || 'Minhas Consultas'}
        onBack={onBack}
        actions={
          <button 
            onClick={fetchConsultations}
            disabled={isLoading}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-300 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        }
      />

      {/* AGC: Filter tabs */}
      <div className="px-4 py-3 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
        <div className="flex gap-2">
          {filters.map(f => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f.id
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* AGC: Content */}
      <div className="p-4 pb-24">
        {/* AGC: Loading */}
        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4 mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded w-1/2" />
              </div>
            ))}
          </div>
        )}

        {/* AGC: Error */}
        {error && !isLoading && (
          <GlassCard>
            <div className="text-center py-6">
              <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-500">{error}</p>
              <button onClick={fetchConsultations} className="mt-4 text-emerald-600">
                Tentar novamente
              </button>
            </div>
          </GlassCard>
        )}

        {/* AGC: Empty state */}
        {!isLoading && !error && consultations.length === 0 && (
          <GlassCard>
            <div className="text-center py-6">
              <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Nenhuma consulta
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {filter === 'all' 
                  ? 'Você ainda não solicitou nenhuma consulta.'
                  : filter === 'active'
                    ? 'Você não tem consultas ativas.'
                    : 'Você não tem consultas concluídas.'
                }
              </p>
              <button
                onClick={handleNewConsultation}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium"
              >
                Buscar Consultores
              </button>
            </div>
          </GlassCard>
        )}

        {/* AGC: Consultations list */}
        {!isLoading && !error && consultations.length > 0 && (
          <div className="space-y-4">
            {consultations.map(consultation => {
              const statusInfo = STATUS_INFO[consultation.status]
              const typeInfo = CONSULTATION_TYPE_INFO[consultation.type]
              const urgencyInfo = URGENCY_INFO[consultation.urgency]
              
              return (
                <button
                  key={consultation.id}
                  onClick={() => handleViewDetail(consultation.id)}
                  className="w-full bg-white dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 transition-colors text-left"
                >
                  {/* AGC: Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-1">
                        {consultation.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {typeInfo.name}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
                      {statusInfo.name}
                    </span>
                  </div>

                  {/* AGC: Description preview */}
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">
                    {consultation.description}
                  </p>

                  {/* AGC: Footer info */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-slate-700">
                    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                      <span className={urgencyInfo.color}>● {urgencyInfo.name}</span>
                      {consultation.bids && consultation.bids.length > 0 && (
                        <span>{consultation.bids.length} proposta(s)</span>
                      )}
                      {consultation.proposedPrice && (
                        <span>R$ {consultation.proposedPrice}</span>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>

                  {/* AGC: Consultant info if assigned */}
                  {consultation.consultantName && (
                    <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700 flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center">
                        <span className="text-white text-xs font-bold">
                          {consultation.consultantName.charAt(0)}
                        </span>
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {consultation.consultantName}
                      </span>
                    </div>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* AGC: FAB for new consultation - safe-area-bottom keeps it above the BottomNav and Android nav bar */}
      <button
        onClick={handleNewConsultation}
        className="fixed right-4 w-14 h-14 bg-emerald-600 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-emerald-700 transition-colors"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  )
}

export default ConsultationsList

